import * as VerdiWebClient from "verdi-web-client";
import { DeviceAPI } from "verdi-web-client/APICommands/DeviceAPI";

import { installLegacyWriteInvalidation } from "../../api/query-cache/invalidate-on-legacy-writes";
import { queryClient } from "../../api/queryClient";
import { URLParams } from "../../utils/url-params";
import FocusContext from "../map-management/focus-context";
import {
    reportAssetDeepLinkFailed,
    resolveAssetDeepLink,
    stashAssetDeepLink,
    takePendingAssetDeepLink,
} from "./asset-deep-link";
import { prefetchSessionData } from "./seed-query-cache-from-session";

export function setupVerdiWebClient() {
    let endpoint: string = import.meta.env.VITE_BACKEND_URL ?? "api.verdiag.com/api";
    // Add a trailing slash to the endpoint if it doesn't have one
    if (endpoint.charAt(endpoint.length - 1) !== "/") {
        endpoint = `${endpoint}/`;
    }

    // Set the endpoints in verdi-web-client's SessionHandler
    VerdiWebClient.SessionHandler.endpoint = endpoint;

    // resetQueries (not invalidateQueries) forces isLoading back to true so components
    // show skeletons immediately rather than rendering stale data from the previous org.
    VerdiWebClient.SessionHandler.onSessionUnload.addListener(() => {
        queryClient.resetQueries();
    });

    // Query for and seed the query cache from the session data
    VerdiWebClient.SessionHandler.onSessionLoad.addListener(() => {
        prefetchSessionData();
    });

    // The fourth cache-writing tier: writes made through legacy models stale the v2 list they belong
    // to. Installed here rather than on session load so it is listening before any entity exists.
    installLegacyWriteInvalidation();
}

/**
 * Sets the curUser appropriately from the URL or from the login token and starts the session load
 */
export async function setAccountAndStartSessionLoad() {
    // Resolved before the non-admin branch below because the endpoint answers for any caller who
    // belongs to the owning organization, not just admins — unlike the device-owner lookup, which is
    // admin-only. A refusal has already been reported, so fall through to a normal load.
    const assetDeepLink = await resolveAssetDeepLink();
    if (assetDeepLink) {
        FocusContext.startLoadHandler("Fetching Account", "settingAccount");
        // Stashed before the switch, not after: changeUser loads the session itself, and that load is
        // what opens the card.
        stashAssetDeepLink(assetDeepLink);
        // changeUser assigns curUser before loading and never rolls it back, so a failed load would
        // otherwise leave every later request addressed to an account we could not open.
        const previousUser = VerdiWebClient.SessionHandler.curUser;
        try {
            await VerdiWebClient.SessionHandler.changeUser(assetDeepLink.identity.organizationId);
            FocusContext.resolveLoadHandler("settingAccount");
            return;
        } catch (e) {
            console.warn("Error setting account from asset parameter", e);
            VerdiWebClient.SessionHandler.curUser = previousUser;
            // Nothing below belongs to this asset's account, so do not leave the link for it to eat.
            takePendingAssetDeepLink();
            reportAssetDeepLinkFailed(assetDeepLink.identity.verdiId);
            FocusContext.resolveLoadHandler("settingAccount");
        }
    }

    // if this isn't an admin, then the account is just whatever their account is.
    if (!VerdiWebClient.SessionHandler.admin) {
        console.debug(`Loading regular user session...`);
        FocusContext.startLoadHandler("Loading Account", "sessionLoad");
        await VerdiWebClient.SessionHandler.loadSession();
        return;
    }

    const accountParam: string = URLParams.getParam("account") ?? "";
    // if it is an admin and there is an account parameter set, then we should use that
    if (accountParam) {
        FocusContext.startLoadHandler("Fetching Account", "settingAccount");
        try {
            await VerdiWebClient.SessionHandler.changeUser(accountParam);
            FocusContext.resolveLoadHandler("settingAccount");
        } catch (e) {
            console.warn("Error setting account", e);
        }
        return;
    }

    const deviceParam = URLParams.getParam("dev");

    // Admins may have no account parameter, but have a device set. Figure out the account based on the device
    if (
        deviceParam &&
        !URLParams.getParam("account") &&
        VerdiWebClient.SessionHandler.admin &&
        !VerdiWebClient.SessionHandler.curUser
    ) {
        console.debug("Resolving account parameter from device owner...");

        DeviceAPI.debuggingDeviceQuery(deviceParam).then((r) => {
            const deviceOwner: string = typeof r?.DeviceDoc?.owner === "string" ? r.DeviceDoc.owner : "";
            if (deviceOwner.length > 0) {
                console.debug(`Resolved device owner to ${deviceOwner}`);
                URLParams.setParam("account", deviceOwner);
                VerdiWebClient.SessionHandler.changeUser(deviceOwner)
                    .then(() => {
                        FocusContext.resolveLoadHandler("settingAccount");
                    })
                    .catch((e) => {
                        console.warn("Error setting account", e);
                    });
            } else {
                console.debug(`Failed to resolve owner from device with ID ${deviceParam}`);
            }
        });
    }
}
